// @flow
import getClientTrackingParams from 'app/packages/client-tracking-params';
import ResourceFactory from 'app/common/lib/ResourceFactory';
import SafeLink from 'app/common/lib/SafeLink';
import type { Entry } from 'app/common/redux/reducers/location';

type Pin = {
  board?: {
    url: string,
  },
  id: string,
  pinner?: {
    username: string,
  },
  tracking_params: string,
  tracking_params_map: Object,
};

type Location = {
  history: Array<Entry>,
  pathname: string,
};

/**
 * NOTE: Opening external links on Windows PWA need special
 * care if we want it to be opened in the user's browser.
 * Because we make the /offsite call first, the PWA doesn't
 * recognize that it's an external link, so by default,
 * it'll open the link inside a PWA window.
 *
 * Instead, we must manually sanitize the url and take
 * that sanitized url and launch it using an WinRT api.
 *
 * NOTE: We already do something similar in ExperimentNavigateLink
 */
export default function openExternalLinkFromPWA({
  url,
  pinId,
  pin,
  location,
  auxData,
}: {
  url: string,
  pinId?: string,
  pin?: Pin,
  location: Location,
  auxData?: Object,
}) {
  const kwargs = {
    check_only: true,
    client_tracking_params: pin ? getClientTrackingParams(pin, location) : undefined,
    pin_id: pin ? pin.id : pinId,
    url,
    aux_data: JSON.stringify(auxData),
  };

  const resource = ResourceFactory.create('OffsiteLinkResource', kwargs);
  resource.callGet().then((response) => {
    if (response && response.resource_response && !response.resource_response.error) {
      const { resource_response: resourceResponse } = response;
      const { redirect_status: redirectStatus, url: sanitizedUrl } = resourceResponse.data;
      const blocked = ['blocked', 'suspicious', 'porn'].includes(redirectStatus);
      if (!blocked) {
        const uri = new window.Windows.Foundation.Uri(sanitizedUrl);
        // Launch the URI using the user's default browser
        window.Windows.System.Launcher.launchUriAsync(uri);
        return;
      }
    }

    // This is the existing behavior where we use
    // SafeLink.open to open a link. In the windows app,
    // this will open in a new PWA window instead of the user's browser.
    if (pin) {
      const clientTrackingParams = getClientTrackingParams(pin, location);
      SafeLink.open(url, pinId, null, clientTrackingParams, auxData);
    } else {
      SafeLink.open(url, pinId);
    }
  });
}
